import { useEffect, useState } from "react";
import { CONCLUDED_PROJECTS, ONGOING_PROJECTS, TECH_FIELDS } from "../data/constants";
import { C } from "../theme/colors";
import { useGSAP } from "../utils/gsapLoader";
import Divider from "../components/Divider";
import Section from "../components/Section";
import PageHero from "../components/PageHero";

const RESEARCH_API_URL = "https://ccapi.rapifuzz.in:8443/api/research/";

export default function ResearchPage() {
  const gsap = useGSAP();
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch(RESEARCH_API_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then(json => {
        if (cancelled) return;
        const record = Array.isArray(json?.data) ? json.data[0] : null;
        if (record) setApiData(record);
      })
      .catch(() => {
        // Silently keep static fallback data on any failure
      });

    return () => { cancelled = true; };
  }, []);

  // Prefer live API data; fall back to the static constants if the API has nothing
  const techFields = apiData?.skills?.length
    ? apiData.skills.map(s => s.skill_name)
    : TECH_FIELDS;

  const concludedProjects = apiData?.concludeds?.length
    ? apiData.concludeds.map(c => c.concluded_name)
    : CONCLUDED_PROJECTS;

  const ongoingProjects = apiData?.ongoings?.length
    ? apiData.ongoings.map(o => o.ongoing_name)
    : ONGOING_PROJECTS;

  const concludedHeading = apiData?.concluded_heading || "Collaborative Research Projects — Concluded";
  const ongoingHeading = apiData?.ongoing_heading || "Collaborative Research Projects — Ongoing";
  const overviewDescription = apiData?.description || null;

  const approachColors = [C.navy, C.teal, C.blue, C.green];
  const staticApproachPoints = [
    {title:"Indigenous Technology",desc:"Home-grown security technologies to reduce foreign dependence"},
    {title:"Collaborative Research",desc:"National and international academic partnerships"},
    {title:"Innovation & Incubation",desc:"Supporting entrepreneurs in national security startups"},
    {title:"Knowledge Exchange",desc:"Joint projects and knowledge-sharing programs"},
  ];
  const approachPoints = apiData?.approchs?.length
    ? apiData.approchs.map(a => ({ title: a.approch_name, desc: a.approch_desc }))
    : staticApproachPoints;

  const researchTabs = [
    { id: "overview", label: "Overview" },
    { id: "techfields", label: "Technology Fields" },
    { id: "concluded", label: "Concluded Projects" },
    { id: "ongoing", label: "Ongoing Projects" },
  ];

  const filteredConcluded = concludedProjects.filter(p => p.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredOngoing = ongoingProjects.filter(p => p.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="page-fade">
      <PageHero title="Research" tag="Innovation for Security" subtitle="CRENS serves as a nodal centre for collaborative research between BITS Pilani's academic faculty and India's national security establishments.">
        <div style={{marginTop:24,display:"flex",gap:20,flexWrap:"wrap"}}>
          {[["71+","Research Projects Funded"],["33","Projects Concluded"],["4","Campuses Engaged"]].map(([n,l],i) => (
            <div key={i} style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,padding:"12px 20px",minWidth:120}}>
              <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:22,fontWeight:700,color:C.goldLight}}>{n}</div>
              <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,color:"rgba(255,255,255,0.6)",letterSpacing:0.5,marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>
      </PageHero>

      <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,position:"sticky",top:66,zIndex:100}}>
        <div style={{maxWidth:1260,margin:"0 auto",padding:"0 40px",display:"flex",gap:0,overflowX:"auto"}}>
          {researchTabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchTerm(""); }}
              style={{background:"none",border:"none",cursor:"pointer",padding:"18px 24px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,fontWeight:activeTab===tab.id?700:500,color:activeTab===tab.id?C.navy:C.textMuted,borderBottom:activeTab===tab.id?`2.5px solid ${C.navy}`:"2.5px solid transparent",transition:"all .2s",whiteSpace:"nowrap"}}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "overview" && (
        <div className="page-fade">
          <Section>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:40,marginBottom:56}} className="grid-2">
              <div>
                <span className="section-tag">Our Approach</span>
                <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:"clamp(1.4rem,2vw,1.9rem)",color:C.navyDark,marginTop:10}}>Collaborative Research for National Security</h2>
                <Divider/>
                {overviewDescription ? (
                  <div className="body" dangerouslySetInnerHTML={{__html: overviewDescription}} />
                ) : (
                  <p className="body">CRENS serves as a nodal centre for collaborative research between BITS Pilani's academic faculty and India's national security establishments. With 71+ research projects funded and 33 concluded, CRENS has established a robust portfolio spanning cybersecurity, quantum technologies, AI, unmanned systems, electronic warfare, and space security.</p>
                )}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                {approachPoints.map((p,i) => {
                  const color = approachColors[i % approachColors.length];
                  return (
                    <div key={i} className="card" style={{padding:20,borderTop:`3px solid ${color}`}}>
                      <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,fontWeight:700,color:color,marginBottom:8}}>{p.title}</div>
                      <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMid,lineHeight:1.7,margin:0}}>{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{background:C.navyDark,borderRadius:16,padding:"48px 40px",marginTop:16}}>
              <div style={{textAlign:"center",marginBottom:36}}>
                <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:C.goldLight}}>Vision Document</span>
                <h3 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:"1.4rem",color:C.white,marginTop:8,marginBottom:8}}>Emerging Technological Fields in National Security Domain</h3>
                <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:"rgba(255,255,255,0.5)"}}>A Vision Document is being prepared highlighting CRENS's focus areas for Research, Innovation and Skill Development</p>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center"}}>
                {techFields.slice(0,16).map((f,i) => (
                  <span key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,padding:"6px 14px",borderRadius:20,background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.75)",cursor:"default"}}>{f}</span>
                ))}
              </div>
            </div>
          </Section>
        </div>
      )}

      {activeTab === "techfields" && (
        <div className="page-fade" style={{padding:"72px 40px",background:C.white}}>
          <div style={{maxWidth:1260,margin:"0 auto"}}>
            <div style={{marginBottom:48}}>
              <span className="section-tag">Focus Areas</span>
              <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",color:C.navyDark,marginTop:10}}>Emerging Technological Fields in National Security</h2>
              <Divider/>
              <p className="body" style={{maxWidth:620}}>CRENS's research spans {techFields.length} cutting-edge technology domains, each critical to India's strategic self-reliance and national security capability.</p>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {techFields.map((f,i) => <span key={i} className="tech-pill">{f}</span>)}
            </div>
          </div>
        </div>
      )}

      {activeTab === "concluded" && (
        <div className="page-fade" style={{padding:"72px 40px",background:C.white}}>
          <div style={{maxWidth:1260,margin:"0 auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:40,flexWrap:"wrap",gap:20}}>
              <div>
                <span className="section-tag">Portfolio</span>
                <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",color:C.navyDark,marginTop:10}}>{concludedHeading}</h2>
                <Divider/>
                <p className="body">{concludedProjects.length} completed projects spanning cybersecurity, AI, unmanned systems, and more.</p>
              </div>
              <input type="search" placeholder="Search projects..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} style={{width:280,flexShrink:0}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}} className="grid-2">
              {filteredConcluded.map((p,i) => (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"14px 18px",background:C.bgAlt,borderRadius:10,border:`1px solid ${C.borderLight}`,transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentDeep;e.currentTarget.style.background=C.accent;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=C.borderLight;e.currentTarget.style.background=C.bgAlt;}}>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:12,color:C.gold,flexShrink:0,minWidth:22}}>{String(i+1).padStart(2,"0")}.</span>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.5}}>{p}</span>
                </div>
              ))}
            </div>
            {filteredConcluded.length === 0 && <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted,fontFamily:"Arial,Helvetica,sans-serif"}}>No projects matching "{searchTerm}"</div>}
          </div>
        </div>
      )}

      {activeTab === "ongoing" && (
        <div className="page-fade" style={{padding:"72px 40px",background:C.white}}>
          <div style={{maxWidth:1260,margin:"0 auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:40,flexWrap:"wrap",gap:20}}>
              <div>
                <span className="section-tag">Active Research</span>
                <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",color:C.navyDark,marginTop:10}}>{ongoingHeading}</h2>
                <Divider/>
                <p className="body">{ongoingProjects.length} active projects currently under execution across BITS Pilani campuses.</p>
              </div>
              <input type="search" placeholder="Search projects..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} style={{width:280,flexShrink:0}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}} className="grid-2">
              {filteredOngoing.map((p,i) => (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"14px 18px",background:"#F0FDF4",borderRadius:10,border:`1px solid #BBF7D0`,transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor="#86EFAC";e.currentTarget.style.background="#DCFCE7";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="#BBF7D0";e.currentTarget.style.background="#F0FDF4";}}>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:12,color:C.green,flexShrink:0,minWidth:22}}>{String(i+1).padStart(2,"0")}.</span>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.5}}>{p}</span>
                </div>
              ))}
            </div>
            {filteredOngoing.length === 0 && <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted,fontFamily:"Arial,Helvetica,sans-serif"}}>No projects matching "{searchTerm}"</div>}
          </div>
        </div>
      )}
    </div>
  );
}